import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { LiveToggle } from "@/components/LiveToggle";
import { useLiveSource } from "@/hooks/useLiveSource";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, Banknote, Eye } from "lucide-react";

const STATUS_BADGE = {
  pending: { variant: "outline", icon: Clock, label: "Pending" },
  approved: { variant: "default", icon: CheckCircle, label: "Approved" },
  rejected: { variant: "destructive", icon: XCircle, label: "Rejected" },
};

export default function AdminPaymentModes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [acting, setActing] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { source, setSource, relayConfigured, isPreview } = useLiveSource();

  const openDetail = async (modeId) => {
    setDetail({ id: modeId, data: null, error: null });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/payment-modes/${modeId}/detail`);
      setDetail({ id: modeId, data, error: null });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message || "Could not load detail";
      toast.error(msg);
      // Keep the dialog open with the error so the user sees what went wrong
      // instead of the click silently doing nothing.
      setDetail({ id: modeId, data: null, error: msg });
    } finally {
      setDetailLoading(false);
    }
  };

  const load = async () => {
    try {
      if (source === "live") {
        const filters = filter ? { status: filter } : {};
        const { data } = await api.post("/admin/system-test/live-db", {
          table: "payment_mode_whitelist", filters, sort_field: "created_at", sort_dir: -1, limit: 200,
        });
        setItems((data.rows || []).map(r => ({
          ...r,
          held_credits_count: 0,
          held_credits_amount: 0,
          user_name: r.user_id,
          user_email: "",
        })));
      } else {
        const params = filter ? `?status=${filter}` : "";
        const { data } = await api.get(`/admin/payment-modes${params}`);
        setItems(data.items || []);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter, source]);

  const act = async (id, action) => {
    setActing(id);
    try {
      const { data } = await api.post(`/admin/payment-modes/${id}/action`, { action });
      if (action === "approve" && (data.released_count || 0) > 0) {
        const totalAmt = (data.released_credits || []).reduce((s, r) => s + Number(r.amount || 0), 0);
        toast.success(`${data.message}`);
      } else {
        toast.success(data.message);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const releaseCredits = async (id) => {
    setReleasing(id);
    try {
      const { data } = await api.post(`/admin/payment-modes/${id}/release-credits`);
      if (data.released_count > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReleasing(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-6 lg:p-10 space-y-6" data-testid="admin-payment-modes">
        <div>
          <p className="overline">ADMIN</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
            Payment Mode Whitelist
          </h1>
          <p className="text-muted-foreground mt-2">
            When a partner or customer uses a new payment instrument, it requires your approval.
            Once approved, future top-ups via that instrument proceed directly. Held credits are
            auto-released on approval — use the "Release credits" button to process any new held
            credits that arrive after approval.
          </p>
          <div className="mt-2">
            <LiveToggle source={source} setSource={setSource} relayConfigured={relayConfigured} isPreview={isPreview} />
          </div>
        </div>

        <div className="flex gap-2" data-testid="mode-filter-tabs">
          {["pending", "approved", "rejected", ""].map((f) => (
            <Button
              key={f || "all"}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => { setFilter(f); setLoading(true); }}
              data-testid={`filter-${f || "all"}`}
            >
              {f || "All"} {f === "pending" && items.length > 0 && filter === "pending" ? `(${items.length})` : ""}
            </Button>
          ))}
        </div>

        <div className="surface-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>Mode Type</TableHead>
                <TableHead>Mode Details</TableHead>
                <TableHead>Held credits</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No entries</TableCell></TableRow>
              ) : items.map((m) => {
                const badge = STATUS_BADGE[m.status] || STATUS_BADGE.pending;
                const Icon = badge.icon;
                const held = Number(m.held_credits_count || 0);
                const heldAmt = Number(m.held_credits_amount || 0);
                return (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="font-medium text-sm">{m.user_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{m.user_email}</div>
                    </TableCell>
                    <TableCell className="uppercase text-xs font-mono">{m.mode_type}</TableCell>
                    <TableCell className="text-sm font-mono">{m.mode_detail}</TableCell>
                    <TableCell data-testid={`held-credits-${m.id}`}>
                      {held > 0 ? (
                        <div className="text-xs">
                          <div className="font-semibold text-amber-700">{held} topup{held > 1 ? "s" : ""}</div>
                          <div className="text-muted-foreground font-mono">₹{heldAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={badge.variant} className="gap-1">
                        <Icon className="h-3 w-3" /> {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => openDetail(m.id)}
                        data-testid={`view-mode-${m.id}`}
                        aria-label="View detail"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {m.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            disabled={acting === m.id}
                            onClick={() => act(m.id, "approve")}
                            data-testid={`approve-mode-${m.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={acting === m.id}
                            onClick={() => act(m.id, "reject")}
                            data-testid={`reject-mode-${m.id}`}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {m.status === "approved" && held > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500 text-amber-700 hover:bg-amber-50 gap-1.5"
                          disabled={releasing === m.id}
                          onClick={() => releaseCredits(m.id)}
                          data-testid={`release-credits-${m.id}`}
                        >
                          <Banknote className="h-3.5 w-3.5" />
                          {releasing === m.id
                            ? "Processing..."
                            : `Release ${held} credit${held > 1 ? "s" : ""} (₹${heldAmt.toLocaleString("en-IN", { maximumFractionDigits: 0 })})`}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Drill-down dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment mode detail</DialogTitle>
            <DialogDescription>Drill-down — partner KYC, all linked top-ups (with PG response), customers funded.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 text-center text-muted-foreground">Loading…</div>
          ) : !detail?.data ? null : (
            <div className="space-y-6">
              {/* Mode + partner */}
              <DetailSection title="Mode">
                <KV label="Type" value={detail.data.mode.mode_type} mono />
                <KV label="Detail" value={detail.data.mode.mode_detail} mono />
                <KV label="Status" value={detail.data.mode.status} />
                <KV label="Approved by" value={detail.data.mode.approved_by || "—"} mono />
                <KV label="Created" value={detail.data.mode.created_at} mono />
              </DetailSection>
              <DetailSection title="Partner">
                <KV label="Business" value={detail.data.partner?.business_name} />
                <KV label="Name" value={detail.data.partner?.full_name} />
                <KV label="Email" value={detail.data.partner?.email} mono />
                <KV label="Mobile" value={detail.data.partner?.mobile} mono />
                <KV label="Role" value={detail.data.partner?.role} />
                <KV label="Wallet ₹" value={detail.data.partner?.wallet_balance} mono />
              </DetailSection>
              <DetailSection title="Partner KYC">
                {!detail.data.partner_kyc ? (
                  <div className="col-span-full text-sm text-muted-foreground">No KYC on file.</div>
                ) : (
                  <>
                    <KV label="Status" value={detail.data.partner_kyc.status} />
                    <KV label="PAN" value={detail.data.partner_kyc.pan} mono />
                    <KV label="Aadhaar (masked)" value={detail.data.partner_kyc.aadhaar_masked || detail.data.partner_kyc.aadhaar} mono />
                    <KV label="Address" value={detail.data.partner_kyc.address} className="md:col-span-3" />
                  </>
                )}
              </DetailSection>
              {/* Linked topups */}
              <div>
                <div className="text-sm font-semibold mb-2">Linked top-ups · partner ({detail.data.partner_topups?.length || 0})</div>
                {(detail.data.partner_topups || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">None.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.data.partner_topups.map((t) => (
                      <details key={t.id} className="bg-muted/30 rounded p-2 text-xs">
                        <summary className="cursor-pointer font-mono">
                          {t.created_at} · {t.gateway} · ₹{t.amount} · {t.status} · {t.provider_order_id || "—"}
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-[10px] bg-background/60 p-2 rounded overflow-auto max-h-60">
                          {JSON.stringify(t.provider_response || t.raw_response || t, null, 2)}
                        </pre>
                      </details>
                    ))}
                  </div>
                )}
              </div>
              {/* Linked customer topups */}
              <div>
                <div className="text-sm font-semibold mb-2">Linked top-ups · customer ({detail.data.customer_topups?.length || 0})</div>
                {(detail.data.customer_topups || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">None.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.data.customer_topups.map((t) => (
                      <details key={t.id} className="bg-muted/30 rounded p-2 text-xs">
                        <summary className="cursor-pointer font-mono">
                          {t.created_at} · +91{t.mobile} · {t.gateway} · ₹{t.amount} · {t.status}
                        </summary>
                        <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-[10px] bg-background/60 p-2 rounded overflow-auto max-h-60">
                          {JSON.stringify(t.provider_response || t.raw_response || t, null, 2)}
                        </pre>
                      </details>
                    ))}
                  </div>
                )}
              </div>
              {/* Customers funded */}
              <div>
                <div className="text-sm font-semibold mb-2">Customers funded ({detail.data.customers?.length || 0})</div>
                {(detail.data.customers || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">None.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {detail.data.customers.map((c) => (
                      <div key={c.mobile} className="bg-muted/30 rounded p-2 text-xs">
                        <div className="font-mono">+91 {c.mobile}</div>
                        <div className="text-muted-foreground">Balance: ₹{c.balance ?? "—"}</div>
                        {c.kyc?.full_name && <div className="mt-1">KYC: <b>{c.kyc.full_name}</b> ({c.kyc.status})</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const DetailSection = ({ title, children }) => (
  <div>
    <div className="text-sm font-semibold mb-2">{title}</div>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 bg-muted/30 rounded p-3">
      {children}
    </div>
  </div>
);
const KV = ({ label, value, mono = false, className = "" }) => (
  <div className={className}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-0.5 text-sm ${mono ? "font-mono break-all" : ""}`}>{value || <em className="text-muted-foreground">—</em>}</div>
  </div>
);
